/**
 * Organized crimes: when a crime is really held up, and what each role is worth to it. Pure; shared by the extension
 * and the Discord bot so both judge the same way.
 */

/**
 * When a crime could really start (owner's report 175145-25c3: "only keep track of real delays"): its ready time, or
 * later when its last seat was filled (Torn's `user.joined_at`). Null while a seat is empty — then nobody in it is
 * holding it up, the faction is short of members. Torn runs a full crime by itself at this time when everyone in it is
 * okay, so once it's past, whoever isn't okay (flying, in hospital, in jail) really is holding it up.
 */
export function ocStartsAt(readyAt: number | null, slots: ReadonlyArray<{ userId: number | null; joinedAt?: number | null }>): number | null {
  if (!readyAt || readyAt <= 0 || !slots.length || slots.some((s) => !s.userId)) return null;
  return Math.max(readyAt, ...slots.map((s) => s.joinedAt ?? 0));
}

/**
 * A "booster" role (owner's suggestion 175519-7f60): one whose own weight in its crime is small, so a low pass rate
 * there hardly hurts — factions put low members in it to raise their pass rate (CPR) fast. Owner (2026-10-20): role
 * specific, not against an average — a role counts as one when its weight is under this share of its crime's total
 * (10% to start; the weights themselves are learned from finished crimes as they come in).
 */
export const BOOSTER_SHARE = 0.1;

export type RoleTag = 'critical' | 'booster' | null;

/**
 * "Critical" (owner, 2026-10-22: "a role that is a chunk above the rest — one that seems to be a leading role"): not just
 * the heaviest role, but the crime's leading group, if it has one. Sort the crime's role weights high → low; the leading
 * group is the fewest top roles (at most half of the crime's roles) whose lightest weighs at least CRITICAL_GAP × the next
 * role down AND at least CRITICAL_OVER_AVG × the crime's average role. No such break → no critical role (a balanced crime);
 * two roles tied at the top both count. Examples (tornprobability's weights): Break the Bank 32/29/14/13/10/3 → Muscle #3
 * and Thief #2; Blast from the Past 35/24/16/12/11/3 → Muscle; Market Forces 29/27/23/16/4 → none; Leave No Trace
 * 37/34/29 → none.
 */
export const CRITICAL_GAP = 1.35;
export const CRITICAL_OVER_AVG = 1.25;

/** The keys of a crime's critical roles (see above); [] when none leads. */
export function criticalKeys(crimeWeights: Record<string, number> | null | undefined): string[] {
  const ws = Object.entries(crimeWeights ?? {})
    .filter(([, w]) => typeof w === 'number' && Number.isFinite(w) && w >= 0)
    .sort((a, b) => b[1] - a[1]);
  if (ws.length < 2) return [];
  const avg = ws.reduce((a, [, v]) => a + v, 0) / ws.length;
  if (avg <= 0) return [];
  for (let k = 1; k <= Math.floor(ws.length / 2); k++) {
    const lightest = ws[k - 1]![1];
    const next = ws[k]![1];
    if (lightest >= CRITICAL_OVER_AVG * avg && lightest >= CRITICAL_GAP * next && lightest > next) return ws.slice(0, k).map(([key]) => key);
  }
  return [];
}

/**
 * What a role is to its crime, from that crime's role weights (`{ role key: weight }`, the learned ones where there are
 * enough finished crimes, else tornprobability's): "critical" when it's in the crime's leading group (criticalKeys), a
 * "booster" when its weight is under BOOSTER_SHARE of the crime's total. Needs at least 2 roles with a weight.
 */
export function roleTag(crimeWeights: Record<string, number> | null | undefined, roleKey: string): RoleTag {
  const ws = Object.entries(crimeWeights ?? {}).filter(([, w]) => typeof w === 'number' && Number.isFinite(w) && w >= 0);
  const w = crimeWeights?.[roleKey];
  if (ws.length < 2 || typeof w !== 'number') return null;
  if (criticalKeys(crimeWeights).includes(roleKey)) return 'critical';
  const total = ws.reduce((a, [, v]) => a + v, 0);
  return total > 0 && w / total < BOOSTER_SHARE ? 'booster' : null;
}
